import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { ASSETS } from "@/lib/projects";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // On home page, navbar starts transparent over hero, then turns white.
  // On other pages, always white.
  const transparent = isHome && !scrolled;
  const navBg = transparent
    ? "bg-transparent"
    : "bg-white/95 backdrop-blur-md border-b border-black/5";
  const textColor = transparent ? "text-white" : "text-black";

  const navItems = [
    { to: "/", label: t.nav.home, testid: "nav-home-link" },
    { to: "/about", label: t.nav.about, testid: "nav-about-link" },
    { to: "/portfolio", label: t.nav.portfolio, testid: "nav-portfolio-link" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact-link" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg} ${textColor}`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
        >
          <div
            className={`w-9 h-9 flex items-center justify-center rounded-sm transition-colors duration-500 ${
              transparent ? "bg-white" : "bg-black"
            }`}
          >
            <span
              className={`font-display text-base font-semibold tracking-tight ${
                transparent ? "text-black" : "text-white"
              }`}
            >
              F<span className="text-[#E11D2E]">D</span>
            </span>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-[15px] font-medium tracking-tight">
              Feenstra<span className="text-[#E11D2E]"> Designs</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] opacity-60 mt-1">
              Visualisation Studio
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-12">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `nav-link text-[12px] uppercase tracking-[0.22em] font-medium ${
                  isActive ? "active" : ""
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* Lang switcher + mobile toggle */}
        <div className="flex items-center gap-6">
          <div
            className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium"
            data-testid="lang-switcher"
          >
            <button
              type="button"
              onClick={() => setLang("nl")}
              data-testid="lang-nl"
              className={`px-1 py-0.5 transition-colors ${
                lang === "nl" ? "text-[#E11D2E]" : "opacity-60 hover:opacity-100"
              }`}
            >
              NL
            </button>
            <span className="opacity-30">/</span>
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-1 py-0.5 transition-colors ${
                lang === "en" ? "text-[#E11D2E]" : "opacity-60 hover:opacity-100"
              }`}
            >
              EN
            </button>
          </div>

          <button
            type="button"
            className="md:hidden"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden bg-white text-black border-t border-black/10"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col px-6 py-6 gap-5">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={`mobile-${it.testid}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] font-medium ${
                    isActive ? "text-[#E11D2E]" : ""
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-4 border-t border-black/10 text-[11px] uppercase tracking-[0.22em]">
              <button
                onClick={() => setLang("nl")}
                data-testid="mobile-lang-nl"
                className={lang === "nl" ? "text-[#E11D2E]" : "opacity-60"}
              >
                NL
              </button>
              <span className="opacity-30">/</span>
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={lang === "en" ? "text-[#E11D2E]" : "opacity-60"}
              >
                EN
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
